import React, { useState, useEffect } from 'react';
import { PlusIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from "../config/apiconfig";


// Email Templates Component
const EmailTemplates = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        subject: '',
        content: ''
    });

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/emails?created_by=${user.id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching email templates:', error);
            setError('Failed to load email templates. Please try again.');
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchTemplates();
        }
    }, [user?.id]);

    const [editingId, setEditingId] = useState(null); // Add this line

    const handleEdit = (template) => {
        setEditingId(template.id);
        setFormData({
            title: template.title,
            subject: template.subject,
            content: template.content
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_BASE_URL}/api/templates/emails/${editingId}`
                : `${API_BASE_URL}/api/templates/emails`;
            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    created_by: user.id
                })
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const result = await response.json();
            
            setTemplates(editingId 
                ? templates.map(t => t.id === editingId ? result : t)
                : [...templates, result]
            );
            
            setShowForm(false);
            setEditingId(null);
            setFormData({ title: '', subject: '', content: '' });
        } catch (error) {
            console.error('Error saving email template:', error);
            setError(editingId ? 'Failed to update template' : 'Failed to create template');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({ title: '', subject: '', content: '' });
    };


    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/emails/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            setTemplates(templates.filter(template => template.id !== id));
        } catch (error) {
            console.error('Error deleting email template:', error);
            setError('Failed to delete email template');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Email Templates</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {showForm ? 'Cancel' : 'Create New Template'}
                </button>
            </div>
            
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}
            
            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={formData.subject}
                                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    rows="6"
                                    required
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Save Template
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileTextIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No email templates found</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Create Your First Template
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {templates.map(template => (
                                <tr key={template.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.title}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.subject}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(template.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button 
                                            onClick={() => handleEdit(template)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(template.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EventTemplates = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // Track which template is being edited
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_type: 'Interview',
        duration: 60
    });
    const [editingTemplate, setEditingTemplate] = useState(null);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/events?created_by=${user.id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching event templates:', error);
            setError('Failed to load event templates. Please try again.');
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchTemplates();
        }
    }, [user?.id]);

    const handleEdit = (template) => {
        setEditingId(template.id);
        setFormData({
            title: template.title,
            description: template.description,
            event_type: template.event_type,
            duration: template.duration
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (editingId) {
                // Update existing template
                response = await fetch(`${API_BASE_URL}/api/templates/events/${editingId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...formData,
                        created_by: user.id
                    })
                });
            } else {
                // Create new template
                response = await fetch(`${API_BASE_URL}/api/templates/events`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...formData,
                        created_by: user.id
                    })
                });
            }
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const updatedTemplate = await response.json();
            
            if (editingId) {
                setTemplates(templates.map(t => 
                    t.id === editingId ? updatedTemplate : t
                ));
            } else {
                setTemplates([...templates, updatedTemplate]);
            }
            
            setShowForm(false);
            setEditingId(null);
            setFormData({
                title: '',
                description: '',
                event_type: 'Interview',
                duration: 60
            });
        } catch (error) {
            console.error('Error saving event template:', error);
            setError(editingId ? 'Failed to update event template' : 'Failed to create event template');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            title: '',
            description: '',
            event_type: 'Interview',
            duration: 60
        });
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/events/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            setTemplates(templates.filter(template => template.id !== id));
        } catch (error) {
            console.error('Error deleting event template:', error);
            setError('Failed to delete event template');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Event Templates</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {showForm ? 'Cancel' : 'Create New Template'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                                <select
                                    value={formData.event_type}
                                    onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="Virtual">Virtual</option>
                                    <option value="On-Site">On-Site</option>

                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                                <input
                                    type="number"
                                    value={formData.duration}
                                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    min="15"
                                    step="15"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Save Template
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileTextIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No event templates found</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Create Your First Template
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {templates.map(template => (
                                <tr key={template.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.title}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.event_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.duration} min</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(template.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <button 
                                        onClick={() => handleEdit(template)}
                                        className="text-blue-600 hover:text-blue-800 mr-3"
                                    >
                                        Edit
                                    </button>
                                        <button 
                                            onClick={() => handleDelete(template.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const BenefitTemplates = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        benefit_type: 'Health',
        value: '',
        is_taxable: false
    });

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/benefits?created_by=${user.id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching benefit templates:', error);
            setError('Failed to load benefit templates. Please try again.');
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchTemplates();
        }
    }, [user?.id]);

    const [editingId, setEditingId] = useState(null);

    const handleEdit = (template) => {
        setEditingId(template.id);
        setFormData({
            title: template.title,
            description: template.description,
            benefit_type: template.benefit_type,
            value: template.value,
            is_taxable: template.is_taxable
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_BASE_URL}/api/templates/benefits/${editingId}`
                : `${API_BASE_URL}/api/templates/benefits`;
            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    created_by: user.id
                })
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const result = await response.json();
            
            setTemplates(editingId 
                ? templates.map(t => t.id === editingId ? result : t)
                : [...templates, result]
            );
            
            setShowForm(false);
            setEditingId(null);
            setFormData({
                title: '',
                description: '',
                benefit_type: 'Health',
                value: '',
                is_taxable: false
            });
        } catch (error) {
            console.error('Error saving benefit template:', error);
            setError(editingId ? 'Failed to update template' : 'Failed to create template');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            title: '',
            description: '',
            benefit_type: 'Health',
            value: '',
            is_taxable: false
        });
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/benefits/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            setTemplates(templates.filter(template => template.id !== id));
        } catch (error) {
            console.error('Error deleting benefit template:', error);
            setError('Failed to delete benefit template');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Benefit Templates</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {showForm ? 'Cancel' : 'Create New Template'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Type</label>
                                <select
                                    value={formData.benefit_type}
                                    onChange={(e) => setFormData({...formData, benefit_type: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="Health">Health</option>
                                    <option value="Retirement">Retirement</option>
                                    <option value="Education">Education</option>
                                    <option value="Wellness">Wellness</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                                <input
                                    type="number"
                                    value={formData.value}
                                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_taxable"
                                    checked={formData.is_taxable}
                                    onChange={(e) => setFormData({...formData, is_taxable: e.target.checked})}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_taxable" className="ml-2 block text-sm text-gray-700">
                                    Is Taxable
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Save Template
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileTextIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No benefit templates found</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Create Your First Template
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taxable</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {templates.map(template => (
                                <tr key={template.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.title}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.benefit_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {template.value ? `$${template.value}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {template.is_taxable ? 'Yes' : 'No'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(template.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button 
                                            onClick={() => handleEdit(template)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(template.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const HolidayTemplates = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        date: '',
        is_recurring: true
    });

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/holidays?created_by=${user.id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching holiday templates:', error);
            setError('Failed to load holiday templates. Please try again.');
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchTemplates();
        }
    }, [user?.id]);

    const [editingId, setEditingId] = useState(null);

    const handleEdit = (template) => {
        setEditingId(template.id);
        setFormData({
            name: template.name,
            date: template.date,
            is_recurring: template.is_recurring
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_BASE_URL}/api/templates/holidays/${editingId}`
                : `${API_BASE_URL}/api/templates/holidays`;
            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    created_by: user.id
                })
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const result = await response.json();
            
            setTemplates(editingId 
                ? templates.map(t => t.id === editingId ? result : t)
                : [...templates, result]
            );
            
            setShowForm(false);
            setEditingId(null);
            setFormData({
                name: '',
                date: '',
                is_recurring: true
            });
        } catch (error) {
            console.error('Error saving holiday template:', error);
            setError(editingId ? 'Failed to update template' : 'Failed to create template');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            name: '',
            date: '',
            is_recurring: true
        });
    };
    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/holidays/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            setTemplates(templates.filter(template => template.id !== id));
        } catch (error) {
            console.error('Error deleting holiday template:', error);
            setError('Failed to delete holiday template');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Holiday Templates</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {showForm ? 'Cancel' : 'Create New Template'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_recurring"
                                    checked={formData.is_recurring}
                                    onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_recurring" className="ml-2 block text-sm text-gray-700">
                                    Recurring annually
                                </label>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Save Template
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileTextIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No holiday templates found</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Create Your First Template
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurring</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {templates.map(template => (
                                <tr key={template.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(template.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {template.is_recurring ? 'Yes' : 'No'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(template.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button 
        onClick={() => handleEdit(template)}
        className="text-blue-600 hover:text-blue-800 mr-3"
    >
        Edit
    </button>
                                        <button 
                                            onClick={() => handleDelete(template.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                  {/* Calendar View */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h3 className="text-lg font-semibold mb-4">Holiday Calendar</h3>
                        <div className="grid grid-cols-7 gap-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                <div key={day} className="text-center font-medium text-gray-500 py-2">
                                    {day}
                                </div>
                            ))}
                            
                            {/* Calendar days would be rendered here */}
                            {Array.from({ length: 35 }).map((_, i) => {
                                const day = i + 1;
                                const holiday = templates.find(t => {
                                    const date = new Date(t.date);
                                    return date.getDate() === day && date.getMonth() === new Date().getMonth();
                                });
                                
                                return (
                                    <div 
                                        key={i} 
                                        className={`p-2 border text-center ${holiday ? 'bg-blue-50' : ''}`}
                                    >
                                        <div className="text-sm">{day <= 31 ? day : ''}</div>
                                        {holiday && (
                                            <div className="text-xs text-blue-600 truncate">{holiday.name}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 text-center">
                            <button className="text-blue-600 hover:text-blue-800">
                                Learn a Holiday Calendar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const CustomFields = () => {
    const { user } = useAuth();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        section: 'Personal',
        field_type: 'text',
        options: '',
        is_required: false
    });

    const fetchFields = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/custom-fields?created_by=${user.id}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setFields(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching custom fields:', error);
            setError('Failed to load custom fields. Please try again.');
            setFields([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            fetchFields();
        }
    }, [user?.id]);

    const [editingId, setEditingId] = useState(null);

    const handleEdit = (field) => {
        setEditingId(field.id);
        setFormData({
            name: field.name,
            section: field.section,
            field_type: field.field_type,
            options: field.options ? field.options.join(', ') : '',
            is_required: field.is_required
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_BASE_URL}/api/templates/custom-fields/${editingId}`
                : `${API_BASE_URL}/api/templates/custom-fields`;
            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    created_by: user.id,
                    options: formData.field_type === 'select' ? formData.options.split(',').map(o => o.trim()) : null
                })
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const result = await response.json();
            
            setFields(editingId 
                ? fields.map(f => f.id === editingId ? result : f)
                : [...fields, result]
            );
            
            setShowForm(false);
            setEditingId(null);
            setFormData({
                name: '',
                section: 'Personal',
                field_type: 'text',
                options: '',
                is_required: false
            });
        } catch (error) {
            console.error('Error saving custom field:', error);
            setError(editingId ? 'Failed to update field' : 'Failed to create field');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            name: '',
            section: 'Personal',
            field_type: 'text',
            options: '',
            is_required: false
        });
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/custom-fields/${id}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            setFields(fields.filter(field => field.id !== id));
        } catch (error) {
            console.error('Error deleting custom field:', error);
            setError('Failed to delete custom field');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Custom Fields</h2>
                <button 
                    onClick={() => setShowForm(!showForm)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {showForm ? 'Cancel' : 'Add Field'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <XCircleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                                <select
                                    value={formData.section}
                                    onChange={(e) => setFormData({...formData, section: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="Personal">Personal</option>
                                    <option value="Employment">Employment</option>
                                    <option value="Education">Education</option>
                                    <option value="Emergency">Emergency Contacts</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                                <select
                                    value={formData.field_type}
                                    onChange={(e) => setFormData({...formData, field_type: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="text">Text</option>
                                    <option value="number">Number</option>
                                    <option value="date">Date</option>
                                    <option value="select">Select</option>
                                    <option value="checkbox">Checkbox</option>
                                </select>
                            </div>
                            {formData.field_type === 'select' && (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma separated)</label>
                                    <input
                                        type="text"
                                        value={formData.options}
                                        onChange={(e) => setFormData({...formData, options: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Option 1, Option 2, Option 3"
                                    />
                                </div>
                            )}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_required"
                                    checked={formData.is_required}
                                    onChange={(e) => setFormData({...formData, is_required: e.target.checked})}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_required" className="ml-2 block text-sm text-gray-700">
                                    Required Field
                                </label>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Save Field
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading fields...</p>
                </div>
            ) : fields.length === 0 ? (
                <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileTextIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No custom fields found</p>
                    <button 
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        Create Your First Field
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Section</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {fields.map(field => (
                                <tr key={field.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{field.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{field.section}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{field.field_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {field.is_required ? 'Yes' : 'No'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(field.updated_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button 
                                            onClick={() => handleEdit(field)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(field.id)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export {
    EmailTemplates,
    EventTemplates,
    BenefitTemplates,
    HolidayTemplates,
    CustomFields
};